import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import CsvDownloadLink from "components/CsvDownloadLink";
import { FiDownload } from "react-icons/fi";

import { userProfilesSelector } from "store/selectors/user";
import type { RootState } from "types/state";

import "./PayrollStatementDownloader.scss";

interface Contribution {
  employeeId: string;
  amount: number;
  skippedAmount?: number;
}

interface PayrollInput {
  contributionsToProcess: Contribution[];
  skippedContributions: Contribution[];
}

interface UserProfile {
  firstName?: string;
  lastName?: string;
  lastFourOfSsn?: string;
  directoryInfo?: {
    location?: string;
    department?: string;
  };
}

interface OwnProps {
  group: { name: string };
  bankAccountNumber: string;
  startDate: string;
  endDate: string;
  totalAmount: number;
  payrollType: string;
  payrollInput: PayrollInput;
  reason?: string;
  text?: string;
}

const mapStateToProps = (state: RootState) => {
  return {
    adminEmail: get(state, "user.auth0User.email") as string,
    userProfiles: userProfilesSelector(state) as Record<string, UserProfile>,
    userProfilesHasMetadata: get(
      state,
      "employer.userProfilesHasMetadata",
      false
    ) as boolean,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

type CsvCell = string | number | boolean | null | undefined;

export class PayrollStatementDownloader extends React.PureComponent<Props> {
  _buildCsvData(): CsvCell[][] {
    const { payrollInput } = this.props;
    const approved = payrollInput.contributionsToProcess.map((contribution) => {
      const userProfile = get(
        this.props.userProfiles,
        contribution.employeeId,
        {}
      ) as UserProfile;
      if (this.props.userProfilesHasMetadata) {
        return [
          userProfile.firstName,
          userProfile.lastName,
          userProfile.lastFourOfSsn,
          get(userProfile, "directoryInfo.location", ""),
          get(userProfile, "directoryInfo.department", ""),
          contribution.amount,
        ];
      }
      return [
        userProfile.firstName,
        userProfile.lastName,
        userProfile.lastFourOfSsn,
        contribution.amount,
      ];
    });
    const withMetadataColumns = [
      "First Name",
      "Last Name",
      "SSN",
      "Location",
      "Department",
      "Amount",
    ];
    const withoutMetadataColumns = ["First Name", "Last Name", "SSN", "Amount"];
    const headers = [
      [
        "Group Name",
        "Bank Account Number",
        "Start",
        "End",
        "Executed By",
        "Type",
        "Reason",
        "Total Amount",
      ],
      [
        this.props.group.name,
        `${this.props.bankAccountNumber}`,
        this.props.startDate,
        this.props.endDate,
        this.props.adminEmail,
        this.props.payrollType,
        this.props.reason || "",
        this.props.totalAmount,
      ],
      [], // new line
    ];
    const approvedContributions = [
      ...headers,
      this.props.userProfilesHasMetadata
        ? withMetadataColumns
        : withoutMetadataColumns,
      ...approved,
    ];
    const hasSkippedContributions = !isEmpty(payrollInput.skippedContributions);
    if (!hasSkippedContributions) {
      return approvedContributions;
    }
    const skippedContributions = payrollInput.skippedContributions.map(
      (contribution) => {
        const userProfile = get(
          this.props.userProfiles,
          contribution.employeeId,
          {}
        ) as UserProfile;
        if (this.props.userProfilesHasMetadata) {
          return [
            userProfile.firstName,
            userProfile.lastName,
            userProfile.lastFourOfSsn,
            get(userProfile, "directoryInfo.location", ""),
            get(userProfile, "directoryInfo.department", ""),
            "Skipped",
            contribution.skippedAmount,
            contribution.amount,
          ];
        }
        return [
          userProfile.firstName,
          userProfile.lastName,
          userProfile.lastFourOfSsn,
          "Skipped",
          contribution.skippedAmount,
          contribution.amount,
        ];
      }
    );
    const withMetadataSkippedColumns = [
      "First Name",
      "Last Name",
      "SSN",
      "Location",
      "Department",
      "Status",
      "Skipped Amount",
      "Amount",
    ];
    const withoutMetadataSkippedColumn = [
      "First Name",
      "Last Name",
      "SSN",
      "Status",
      "Skipped Amount",
      "Amount",
    ];
    if (isEmpty(approved)) {
      return [
        ...headers,
        this.props.userProfilesHasMetadata
          ? withMetadataSkippedColumns
          : withoutMetadataSkippedColumn,
        ...skippedContributions,
      ];
    }
    return [
      ...approvedContributions,
      [], // new line
      this.props.userProfilesHasMetadata
        ? withMetadataSkippedColumns
        : withoutMetadataSkippedColumn,
      ...skippedContributions,
    ];
  }

  _buildFilename() {
    return `${this.props.group.name}-${this.props.startDate}-${this.props.endDate}`;
  }

  render() {
    return (
      <span className="csv-downloader">
        <CsvDownloadLink
          data={this._buildCsvData()}
          filename={this._buildFilename()}
        >
          <div className="downloader-description">
            <FiDownload
              fill={"#FFFFFF"}
              color={"#FFFFFF"}
              stroke={"#29b0c2"}
              size={"16px"}
            />
            <p className="downloader-text"> {this.props.text}</p>
          </div>
        </CsvDownloadLink>
      </span>
    );
  }
}

export default connector(withApollo<Props>(PayrollStatementDownloader));
